import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { useCallback, useEffect, useState, useMemo, memo, Fragment } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react"
import { Outlet, useFetcher, useLoaderData, useRouteLoaderData, useRevalidator } from "react-router"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { hasPermission } from "@/machines/Authmachine"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import type { RootLoader } from "@/routes/root"

const routeId = "evidence-counts"
const sessionKey = "evidence-counts-filter-storage"

const CRITERIA_TYPE_OPTIONS = ["Bookmark", "Classification", "EvidenceCount", "Tag"] as const
const CRITERIA_TYPE_OPTION_LABELS = ["Bookmark", "Classification", "Evidence Count", "Tag"] as const
const SCOPE_OPTIONS = ["Record", "Instance"] as const

type CriteriaType = (typeof CRITERIA_TYPE_OPTIONS)[number]
type ScopeType = (typeof SCOPE_OPTIONS)[number]

interface EvidenceCount {
  name: string
  criteriaLabel: string
  criteriaType: CriteriaType
  ruleScope: ScopeType
}

interface LoaderData {
  loadedClientName: string
  clients: string[]
  brands: string[]
  loadedBrand: string
  evidenceCounts: EvidenceCount[]
  hasError: boolean
}

interface ActionData {
  action: string
  errorMessage?: string
}

const useEvidenceCountData = () => {
  const data = useRouteLoaderData<LoaderData>(routeId)
  return useMemo(() => {
    if (data == null) return null
    return {
      ...data,
      criteriaTypeOptions: CRITERIA_TYPE_OPTIONS,
      scopeOptions: SCOPE_OPTIONS,
    }
  }, [data])
}

async function loader(): Promise<LoaderData> {
  try {
    const organisations = getOrganisations()
    if (organisations.length < 1) {
      return {
        loadedClientName: "",
        clients: [],
        brands: [],
        loadedBrand: "",
        evidenceCounts: [],
        hasError: true,
      }
    }

    const sessionData = sessionStorage.getItem(sessionKey)
    const hasSessionData = sessionData !== null && sessionData !== "" && sessionData.length > 0
    const filters = hasSessionData ? (qs.parse(sessionData) as unknown as Partial<StoredSession>) : {}
    const loadedClientName = filters.raw?.organisation ?? organisations[0]

    const brands = getProjectsByOrganisation(loadedClientName)

    const loadedBrand =
      filters.raw?.project != null && brands.includes(filters.raw.project) ? filters.raw.project : (brands[0] ?? "")

    if (loadedBrand === "") {
      return {
        loadedClientName,
        clients: organisations,
        brands,
        loadedBrand: "",
        evidenceCounts: [],
        hasError: true,
      }
    }

    let evidenceCounts: EvidenceCount[] = []
    let hasError = false

    try {
      const evidenceCountUrl = `/api/unityviewer/evidenceCount/${loadedClientName}/${loadedBrand}`
      const response = await fetchAPI<EvidenceCount[]>(evidenceCountUrl).then(handleResponse)
      evidenceCounts = response ?? []
    } catch {
      hasError = true
      evidenceCounts = []
    }

    return {
      loadedClientName,
      clients: organisations,
      brands,
      loadedBrand,
      evidenceCounts,
      hasError,
    }
  } catch {
    return {
      loadedClientName: "",
      clients: [],
      brands: [],
      loadedBrand: "",
      evidenceCounts: [],
      hasError: true,
    }
  }
}

async function action({ request }: { request: Request }): Promise<ActionData> {
  try {
    const formData = await request.formData()
    const formEntries = Object.fromEntries(formData) as Record<string, string>
    const { action, organisation, project, name, criteriaLabel, criteriaType, ruleScope } = formEntries

    if (organisation == null || organisation.length === 0) {
      return { action, errorMessage: "Missing organisation" }
    }
    if (project == null || project.length === 0) {
      return { action, errorMessage: "Missing project" }
    }

    switch (action) {
      case "CREATE": {
        if (name == null || name.trim().length === 0) {
          return { action, errorMessage: "Name is required" }
        }
        if (ruleScope == null || ruleScope.length === 0) {
          return { action, errorMessage: "Scope is required" }
        }
        if (criteriaLabel == null || criteriaLabel.trim().length === 0) {
          return { action, errorMessage: "Criteria Label is required" }
        }
        if (criteriaType == null || criteriaType.length === 0) {
          return { action, errorMessage: "Criteria Type is required" }
        }

        if (
          CRITERIA_TYPE_OPTIONS.findIndex(
            (ct) => ct.localeCompare(criteriaType, undefined, { sensitivity: "base" }) === 0,
          ) === -1
        ) {
          return { action, errorMessage: "Invalid Criteria Type" }
        }
        if (
          SCOPE_OPTIONS.findIndex((s) => s.localeCompare(ruleScope, undefined, { sensitivity: "base" }) === 0) === -1
        ) {
          return { action, errorMessage: "Invalid Scope" }
        }

        try {
          const trimmedCriteriaLabel = criteriaLabel.trim()
          if (trimmedCriteriaLabel.length === 0) {
            return { action, errorMessage: "Criteria Label cannot be empty" }
          }

          await fetchAPI(`/api/unityviewer/evidenceCount/${organisation}/${project}`, {
            method: "POST",
            body: {
              name: name.trim(),
              criteriaLabel: trimmedCriteriaLabel,
              criteriaType: criteriaType,
              ruleScope: ruleScope,
            },
          }).then(throwIfNotOk)
          return { action }
        } catch (error) {
          if ((error as { status?: number })?.status === 409) {
            return { action, errorMessage: "Evidence Count with this name already exists" }
          }
          return { action, errorMessage: "Error creating Evidence Count" }
        }
      }

      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch {
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

async function mainAction({ request }: { request: Request }): Promise<ActionData> {
  try {
    const formData = await request.formData()
    const formEntries = Object.fromEntries(formData) as Record<string, string>
    const {
      action,
      organisation,
      project,
      name,
      criteriaLabel,
      criteriaType,
      ruleScope,
      newCriteriaLabel,
      newCriteriaType,
      newRuleScope,
    } = formEntries

    if (organisation == null || organisation.length === 0) {
      return { action, errorMessage: "Missing organisation" }
    }
    if (project == null || project.length === 0) {
      return { action, errorMessage: "Missing project" }
    }

    switch (action) {
      case "DELETE": {
        try {
          if (name == null || name.length === 0) {
            return { action, errorMessage: "Missing name for deletion" }
          }

          await fetchAPI(`/api/unityviewer/evidenceCount/delete/${organisation}/${project}`, {
            method: "POST",
            body: {
              name,
              criteriaLabel,
              criteriaType: criteriaType,
              ruleScope: ruleScope,
            },
          }).then(throwIfNotOk)
          return { action }
        } catch {
          return { action, errorMessage: "Couldn't delete Evidence Count" }
        }
      }

      case "EDIT": {
        try {
          if (name == null || name.length === 0) {
            return { action, errorMessage: "Missing name" }
          }

          if (newRuleScope == null || newRuleScope.length === 0) {
            return { action, errorMessage: "Scope is required" }
          }

          if (newCriteriaLabel == null || newCriteriaLabel.trim().length === 0) {
            return { action, errorMessage: "Criteria Label is required" }
          }

          if (newCriteriaType == null || newCriteriaType.length === 0) {
            return { action, errorMessage: "Criteria Type is required" }
          }

          if (
            CRITERIA_TYPE_OPTIONS.findIndex(
              (ct) => ct.localeCompare(newCriteriaType, undefined, { sensitivity: "base" }) === 0,
            ) === -1
          ) {
            return { action, errorMessage: "Invalid Criteria Type" }
          }
          if (
            SCOPE_OPTIONS.findIndex((s) => s.localeCompare(newRuleScope, undefined, { sensitivity: "base" }) === 0) ===
            -1
          ) {
            return { action, errorMessage: "Invalid Scope" }
          }

          const trimmedNewCriteriaLabel = newCriteriaLabel.trim()
          if (trimmedNewCriteriaLabel.length === 0) {
            return { action, errorMessage: "Criteria Label cannot be empty" }
          }

          await fetchAPI(`/api/unityviewer/evidenceCount/${organisation}/${project}/${name}`, {
            method: "PUT",
            body: {
              criteriaLabel,
              criteriaType: criteriaType,
              ruleScope: ruleScope,
              newCriteriaLabel,
              newCriteriaType: newCriteriaType,
              newRuleScope: newRuleScope,
            },
          }).then(throwIfNotOk)

          return { action }
        } catch (error) {
          if ((error as { status?: number })?.status === 304) {
            return { action, errorMessage: "Evidence Count with these details already exists" }
          }
          return { action, errorMessage: "Error updating Evidence Count" }
        }
      }

      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch {
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

export function EvidenceCounts() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useEvidenceCountData()
  const revalidator = useRevalidator()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>(
    (formData: FormData, reason: { type: "field"; name: string } | { type: "submitButton" }) => {
      if (reason.type === "field" && reason.name === "organisation") {
        formData.delete("project")
        setTimeout(() => void revalidator.revalidate(), 100)
      }
    },
    [revalidator],
  )

  if (rootData == null || data == null || revalidator.state === "loading") {
    return (
      <div className="flex size-full flex-col gap-y-4 overflow-x-auto px-20 py-5">
        <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Evidence Counts</h1>
        <div className="flex items-center justify-center py-8 text-gray-500">
          <LoaderCircleIcon className="mr-2 size-4 animate-spin" />
          {revalidator.state === "loading" ? "Updating data..." : "Loading..."}
        </div>
      </div>
    )
  }

  const hasOrganisations = rootData.organisations != null && rootData.organisations.length > 1
  const hasBrands = data.brands != null && data.brands.length > 1

  return (
    <Fragment>
      <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Evidence Counts</h1>
      <div className="flex items-center gap-4">
        <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
          {hasOrganisations && (
            <FiltersWrapper.Select
              name="organisation"
              label="Client Name"
              defaultValue={data.loadedClientName}
              items={data.clients}
            />
          )}
          {hasBrands && (
            <FiltersWrapper.Select name="project" label="Brand" defaultValue={data.loadedBrand} items={data.brands} />
          )}
        </FiltersWrapper>
        <div className="flex-1" />
        <EvidenceCountAddNew data={data} />
      </div>
      <Outlet />
    </Fragment>
  )
}

const EvidenceCountRow = memo(
  ({
    evidenceCount,
    index,
    data,
  }: {
    evidenceCount: EvidenceCount
    index: number
    data: ReturnType<typeof useEvidenceCountData>
  }) => {
    if (data == null) return null

    const criteriaTypeIndex = CRITERIA_TYPE_OPTIONS.findIndex(
      (ct) => ct.localeCompare(evidenceCount.criteriaType, undefined, { sensitivity: "base" }) === 0,
    )
    const ruleScopeIndex = SCOPE_OPTIONS.findIndex(
      (s) => s.localeCompare(evidenceCount.ruleScope, undefined, { sensitivity: "base" }) === 0,
    )

    return (
      <TableRow className="text-sm">
        <TableCell>{index + 1}.</TableCell>
        <TableCell className="font-medium">{evidenceCount.name}</TableCell>
        <TableCell>{evidenceCount.criteriaLabel}</TableCell>
        <TableCell>{CRITERIA_TYPE_OPTION_LABELS[criteriaTypeIndex]}</TableCell>
        <TableCell>{SCOPE_OPTIONS[ruleScopeIndex]}</TableCell>
        <TableCell className="space-x-2">
          <EvidenceCountEdit data={data} evidenceCount={evidenceCount} />
          <EvidenceCountDelete data={data} evidenceCount={evidenceCount} />
        </TableCell>
      </TableRow>
    )
  },
)
EvidenceCountRow.displayName = "EvidenceCountRow"

function Main() {
  const rawData = useLoaderData<LoaderData>()
  const revalidator = useRevalidator()

  const data = useMemo(() => {
    if (rawData == null) return null
    return {
      ...rawData,
      criteriaTypeOptions: CRITERIA_TYPE_OPTIONS,
      scopeOptions: SCOPE_OPTIONS,
    }
  }, [rawData])

  const tableContent = useMemo(() => {
    if (revalidator.state === "loading") {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-gray-500">
            <div className="flex items-center justify-center gap-2">
              <LoaderCircleIcon className="size-4 animate-spin" />
              Loading updated data...
            </div>
          </TableCell>
        </TableRow>
      )
    }

    if (data == null) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-gray-500">
            Error loading data. Please refresh the page.
          </TableCell>
        </TableRow>
      )
    }

    if (data.hasError === true) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-gray-500">
            Error loading Evidence Counts. Please try different selections or add a new Evidence Count.
          </TableCell>
        </TableRow>
      )
    }

    if (data.evidenceCounts == null || data.evidenceCounts.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-gray-500">
            No Evidence Counts found for the selected Client and Brand.
          </TableCell>
        </TableRow>
      )
    }

    return data.evidenceCounts.map((evidenceCount, index) => (
      <EvidenceCountRow
        key={`${evidenceCount.name}-${index}`}
        evidenceCount={evidenceCount}
        index={index}
        data={data}
      />
    ))
  }, [data, revalidator.state])

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <span className="sr-only">No.</span>
          </TableHead>
          <TableHead className="text-left">Name</TableHead>
          <TableHead className="text-left">Criteria Label</TableHead>
          <TableHead className="text-left">Criteria Type</TableHead>
          <TableHead className="text-left">Scope</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{tableContent}</TableBody>
    </Table>
  )
}

const EvidenceCountAddNew = memo(({ data }: { data: ReturnType<typeof useEvidenceCountData> }) => {
  const [open, setOpen] = useState(false)

  const canWrite = hasPermission("writeAccess")

  if (data == null || canWrite === false) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add Evidence Count
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Evidence Count</DialogTitle>
        </DialogHeader>
        <AddNewForm data={data} onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
})
EvidenceCountAddNew.displayName = "EvidenceCountAddNew"

function AddNewForm({ data, onClose }: { data: ReturnType<typeof useEvidenceCountData>; onClose: () => void }) {
  const fetcher = useFetcher<ActionData>()
  const [createAnother, setCreateAnother] = useState(false)
  const [selectedScope, setSelectedScope] = useState("")
  const [criteriaLabel, setCriteriaLabel] = useState("")

  const isLoading = fetcher.state !== "idle"
  const isSuccess = fetcher.state === "idle" && fetcher.data?.action === "CREATE" && fetcher.data.errorMessage == null

  const handleScopeChange = useCallback((scope: string) => {
    setSelectedScope(scope)
  }, [])

  useEffect(() => {
    if (isSuccess === true) {
      if (createAnother === true) {
        setSelectedScope("")
        setCriteriaLabel("")
      } else {
        onClose()
      }
    }
  }, [isSuccess, createAnother, onClose])

  if (data == null) return null

  const isScopeSelected = selectedScope.length > 0
  const isCriteriaLabelValid = criteriaLabel.trim().length > 0

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="CREATE" />
      <input type="hidden" name="organisation" value={data.loadedClientName} />
      <input type="hidden" name="project" value={data.loadedBrand} />

      <div>
        <label className="text-sm font-medium">Name</label>
        <Input required type="text" name="name" placeholder="Enter unique name" className="w-full" />
        <p className="mt-1 text-xs text-gray-500">Must be unique and cannot be changed after creation</p>
      </div>

      <div>
        <label className="text-sm font-medium">Scope</label>
        <Select name="ruleScope" required value={selectedScope} onValueChange={handleScopeChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Scope" />
          </SelectTrigger>
          <SelectContent>
            {SCOPE_OPTIONS.map((scope) => (
              <SelectItem key={scope} value={scope}>
                {scope}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium">Criteria Label *</label>
        <Input
          required
          type="text"
          name="criteriaLabel"
          value={criteriaLabel}
          onChange={(e) => setCriteriaLabel(e.target.value)}
          placeholder="Enter criteria label"
          className="w-full"
        />
        <p className="mt-1 text-xs text-gray-500">Required field - enter the criteria label</p>
      </div>

      <div>
        <label className="text-sm font-medium">Criteria Type</label>
        <Select name="criteriaType" required>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Criteria Type" />
          </SelectTrigger>
          <SelectContent>
            {CRITERIA_TYPE_OPTIONS.map((type, index) => (
              <SelectItem key={type} value={type}>
                {CRITERIA_TYPE_OPTION_LABELS[index]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2 border-t pt-2">
        <input
          type="checkbox"
          id="create-another"
          checked={createAnother}
          onChange={(e) => setCreateAnother(e.target.checked)}
          className="rounded border border-input"
        />
        <label htmlFor="create-another" className="text-sm leading-none font-medium">
          Create another?
        </label>
      </div>

      <div className="flex items-center justify-start gap-4">
        {isLoading && (
          <span className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Creating...
          </span>
        )}
        {fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>}
        {isScopeSelected === false && <p className="text-sm text-amber-600">Please select a scope to continue</p>}
        {isScopeSelected === true && isCriteriaLabelValid === false && (
          <p className="text-sm text-amber-600">Please enter a criteria label to continue</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={isScopeSelected === false || isCriteriaLabelValid === false}>
          Create Evidence Count
        </Button>
      </div>
    </fetcher.Form>
  )
}

const EvidenceCountEdit = memo(
  ({ data, evidenceCount }: { data: ReturnType<typeof useEvidenceCountData>; evidenceCount: EvidenceCount }) => {
    const [isOpen, setIsOpen] = useState(false)

    const canWrite = hasPermission("writeAccess")

    if (data == null || canWrite === false) return null

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Edit Evidence Count">
            <PencilIcon />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Evidence Count</DialogTitle>
          </DialogHeader>
          <EditForm data={data} evidenceCount={evidenceCount} onClose={() => setIsOpen(false)} />
        </DialogContent>
      </Dialog>
    )
  },
)
EvidenceCountEdit.displayName = "EvidenceCountEdit"

function EditForm({
  data,
  evidenceCount,
  onClose,
}: {
  data: ReturnType<typeof useEvidenceCountData>
  evidenceCount: EvidenceCount
  onClose: () => void
}) {
  const fetcher = useFetcher<ActionData>()

  const findMatchingScope = (inputScope: string): string => {
    const found = SCOPE_OPTIONS.find(
      (option) => option.localeCompare(inputScope, undefined, { sensitivity: "base" }) === 0,
    )
    return found ?? SCOPE_OPTIONS[0]
  }

  const findMatchingType = (inputType: string): string => {
    const found = CRITERIA_TYPE_OPTIONS.find(
      (option) => option.localeCompare(inputType, undefined, { sensitivity: "base" }) === 0,
    )
    return found ?? CRITERIA_TYPE_OPTIONS[0]
  }

  const [scope, setScope] = useState(findMatchingScope(evidenceCount.ruleScope))
  const [type, setType] = useState(findMatchingType(evidenceCount.criteriaType))
  const [label, setLabel] = useState(evidenceCount.criteriaLabel)

  const isLoading = fetcher.state !== "idle"
  const isSuccess = fetcher.state === "idle" && fetcher.data?.action === "EDIT" && fetcher.data.errorMessage == null

  const handleScopeChange = useCallback((newScope: string) => {
    setScope(newScope)
  }, [])

  useEffect(() => {
    if (isSuccess === true) onClose()
  }, [isSuccess, onClose])

  if (data == null) return null

  const isScopeValid = scope.length > 0
  const isLabelValid = label != null && label.trim().length > 0
  const isTypeValid = type.length > 0

  return (
    <fetcher.Form method="PATCH" className="space-y-4">
      <input type="hidden" name="action" value="EDIT" />
      <input type="hidden" name="organisation" value={data.loadedClientName} />
      <input type="hidden" name="project" value={data.loadedBrand} />
      <input type="hidden" name="name" value={evidenceCount.name} />
      <input type="hidden" name="criteriaLabel" value={evidenceCount.criteriaLabel} />
      <input type="hidden" name="criteriaType" value={evidenceCount.criteriaType} />
      <input type="hidden" name="ruleScope" value={evidenceCount.ruleScope} />

      <div>
        <label className="text-sm font-medium">Name (read-only)</label>
        <Input type="text" value={evidenceCount.name} className="w-full bg-gray-50" disabled readOnly />
        <p className="mt-1 text-xs text-gray-500">Name cannot be changed after creation</p>
      </div>

      <div>
        <label className="text-sm font-medium">Scope</label>
        <Select name="newRuleScope" value={scope} onValueChange={handleScopeChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCOPE_OPTIONS.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium">Criteria Label *</label>
        <Input
          required
          type="text"
          name="newCriteriaLabel"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Enter criteria label"
          className="w-full"
        />
        <p className="mt-1 text-xs text-gray-500">Required field - enter the criteria label</p>
      </div>

      <div>
        <label className="text-sm font-medium">Criteria Type</label>
        <Select name="newCriteriaType" value={type} onValueChange={setType}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CRITERIA_TYPE_OPTIONS.map((option, index) => (
              <SelectItem key={option} value={option}>
                {CRITERIA_TYPE_OPTION_LABELS[index]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-start gap-4">
        {isLoading && (
          <span className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Updating...
          </span>
        )}
        {fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit" disabled={isScopeValid === false || isLabelValid === false || isTypeValid === false}>
          Update Evidence Count
        </Button>
      </div>
    </fetcher.Form>
  )
}

const EvidenceCountDelete = memo(
  ({ data, evidenceCount }: { data: ReturnType<typeof useEvidenceCountData>; evidenceCount: EvidenceCount }) => {
    const canDelete = hasPermission("deleteAccess")

    if (data == null || canDelete === false) return null

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Delete Evidence Count">
            <TrashIcon className="text-red-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="end" className="max-w-sm">
          <p className="text-sm text-pretty">
            Delete Evidence Count <span className="font-semibold">&quot;{evidenceCount.name}&quot;</span>?
          </p>
          <DeleteForm data={data} evidenceCount={evidenceCount} />
        </PopoverContent>
      </Popover>
    )
  },
)
EvidenceCountDelete.displayName = "EvidenceCountDelete"

function DeleteForm({
  data,
  evidenceCount,
}: {
  data: ReturnType<typeof useEvidenceCountData>
  evidenceCount: EvidenceCount
}) {
  const fetcher = useFetcher<ActionData>()

  if (data == null) return null

  const isLoading = fetcher.state !== "idle"

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE" />
      <input type="hidden" name="organisation" value={data.loadedClientName} />
      <input type="hidden" name="project" value={data.loadedBrand} />
      <input type="hidden" name="name" value={evidenceCount.name} />
      <input type="hidden" name="criteriaLabel" value={evidenceCount.criteriaLabel} />
      <input type="hidden" name="criteriaType" value={evidenceCount.criteriaType} />
      <input type="hidden" name="ruleScope" value={evidenceCount.ruleScope} />

      {isLoading && (
        <span className="flex items-center gap-1">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      )}
      {fetcher.data?.action === "DELETE" && fetcher.data.errorMessage != null && (
        <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm
      </Button>
    </fetcher.Form>
  )
}

EvidenceCounts.loader = loader
EvidenceCounts.action = action
EvidenceCounts.Main = Main
EvidenceCounts.routeId = routeId

Main.action = mainAction
